import {
  ApiException,
  FactFindDetailsDto,
  CompanyDto,
  CompanyDetailsDto
} from "@/api/ClientPortalApiClient";
import {
  UserDto,
  AuthenticateUserCommand,
  RefreshTokenCommand,
  UserType,
  ConfirmTermsAndServiceCommand
} from "@/api/AuthenticationApiClient";
import {
  Module,
  VuexModule,
  MutationAction,
  Action,
  Mutation
} from "vuex-module-decorators";
import ApiFactory from "@/api/ApiFactory";
import { RoleStore, LoadingSpinnerService } from "@/store";
import StatusCodes from "http-status-codes";

export interface IUserState {
  loggedInUser: UserDto | null;
  profile: FactFindDetailsDto | null;
  company: CompanyDto | null;
  companyDetails: CompanyDetailsDto | null;
  sessionTimer: number;
  showSessionTimeout: boolean;
}

@Module({ name: "userStore" })
export class UserStoreModule extends VuexModule implements IUserState {
  companyDetails: CompanyDetailsDto | null = null;
  company: CompanyDto | null = null;
  profile: FactFindDetailsDto | null = null;
  isAgent: boolean = false;
  loggedInUser: UserDto | null = null;
  sessionTimer: number = 0;
  showSessionTimeout: boolean = false;
  isTerm: boolean=false;
  password: string | undefined;


  get isUserLoggedIn(): boolean {
    return this.loggedInUser !== null;
  }

  get getUserName(): string | undefined {
    return this.loggedInUser ? this.loggedInUser.username : undefined;
  }
  get getPassword(): string | undefined {
    return this.password!=""? this.password : undefined;
  }

  get getJWT(): string | undefined {
    return this.loggedInUser ? this.loggedInUser.token : undefined;
  }

  get refreshToken(): string | undefined {
    return this.loggedInUser ? this.loggedInUser.refreshToken : undefined;
  }

  get profileId(): number {
    return this.loggedInUser ? this.loggedInUser.profileId : -1;
  }
  get clientSelectorId(): number {
    return this.loggedInUser ? this.loggedInUser.fK_ClientSelectorID : -1;
  }
  get userId(): string {
    return this.loggedInUser && this.loggedInUser.id
      ? this.loggedInUser.id
      : "";
  }

  get companyName(): string {
    if (this.companyDetails) {
      return this.companyDetails.name || "";
    }
    return "";
  }

  get companyLogo(): string {
    if (this.companyDetails) {
      return this.companyDetails.logo || "";
    }
    return "";
  }


  get companyDatabaseId(): number | undefined {
    if (this.companyDetails) {
      return this.companyDetails.databaseId;
    }
    return undefined;
  }

  get clientFullName(): string {
    if (this.profile) {
      let name = `${this.profile.firstName} ${this.profile.lastName}`;
      if ((this.profile.sP_FirstName || "").length > 0) {
        name = `${name} & ${this.profile.sP_FirstName} ${
          this.profile.sP_LastName
        }`;
      }
      return name;
    } else {
      return "";
    }
  }

  get ShowSessionTimeout(): boolean {
    return this.showSessionTimeout;
  }

  @Mutation
  private user_logged_in(user: UserDto) {
    this.loggedInUser = user;
    this.isTerm =user.isTermsAccept;
   }


  @Mutation
  private async AcceptTerms(user: UserDto) {
  
        this.isTerm =user.isTermsAccept;
  }

  @Mutation
  private profile_loaded(profile: FactFindDetailsDto) {
    this.profile = profile;
  }

  @Mutation
  private company_loaded(company: CompanyDto) {
    this.company = company;
  }

  @Mutation
  private user_logged_out() {
    this.loggedInUser = null;
    this.profile = null;
    this.company = null;
    this.showSessionTimeout = false;
    clearTimeout(this.sessionTimer);
    this.sessionTimer = 0;
  }

  @Mutation company_details_loaded(companyDetails: CompanyDetailsDto | null) {
    this.companyDetails = companyDetails;
  }

  @Mutation
  private refresh_session_timer() {
    this.showSessionTimeout = false;

    if (this.loggedInUser !== null) {
      if (this.sessionTimer > 0) {
        clearTimeout(this.sessionTimer);
      }
      this.sessionTimer = setTimeout(() => {
        this.showSessionTimeout = true;
      }, 60*45*1000);
    } else {
      clearTimeout(this.sessionTimer);
    }
  }
  
  @Action
  async AuthenticateUser(credentials: AuthenticateUserCommand): Promise<UserDto> {
    let result: UserDto;
    const apiAuthClient = ApiFactory.CreateAuthClient();
    result = await apiAuthClient.users_Authenticate(credentials);
     return result;
  }
  @Action
  async ConfirmTerms(credentials: ConfirmTermsAndServiceCommand): Promise<UserDto> {
    let result: UserDto;
    const apiAuthClient = ApiFactory.CreateAuthClient();
    result = await apiAuthClient.users_Terms(credentials);
     return result;
  }

  @Action
  async Login(user: UserDto): Promise<boolean> {
    // let result: UserDto;
    try {
      LoadingSpinnerService.Show("Logging in...");
      const apiDataClient = ApiFactory.CreateDataClient();

      if (user) {
        // Contact owner attempted to log in
        if (
          user.userType !== UserType.Contact &&
          user.userType !== UserType.ExternalBroker &&
          user.userType !== UserType.ContactOwner &&// Maybe better to check if Admin
          user.userType !== UserType.BrokerContact
        ) {
          LoadingSpinnerService.Failure(
            "WealthTrack credentials are not valid here"
          );
          return false;
        }
    
        this.user_logged_in(user);
        await RoleStore.LoadUserRoles();

        if (user.userType === UserType.Contact) {
          const profile = await apiDataClient.factFindDetails_IndexGET(user.profileId);
          if (profile) {
            this.profile_loaded(profile);
          }
        }

        LoadingSpinnerService.Hide();
        return true;
      } else {
        LoadingSpinnerService.Failure("Username or password is incorrect");
        return false;
      }
    } catch (e) {
      const ex: ApiException = e as ApiException;
      if (
        ex.status !== StatusCodes.UNAUTHORIZED &&
        ex.status !== StatusCodes.BAD_REQUEST
      ) {
        LoadingSpinnerService.Failure(
          "Error connecting to authentication server. Please try again."
        );
      } else {
        LoadingSpinnerService.Failure("Username or password is incorrect");
      }

      return false;
    }
  }
  @Action
  async Confirm(user: UserDto): Promise<boolean> {
   
    this.AcceptTerms(user);
    return true;
  }

  @Action
  async Logout(): Promise<boolean> {
    try {
      const apiClient = ApiFactory.CreateAuthClient();

      //const result = await apiClient.users_Authenticate(credentials);
      this.user_logged_out();
      return true;
    } catch (e) {
      const ex: ApiException = e as ApiException;
      if (
        ex.status !== StatusCodes.UNAUTHORIZED &&
        ex.status !== StatusCodes.BAD_REQUEST
      ) {
        LoadingSpinnerService.Failure(
          "Error connecting to authentication server. Please try again."
        );
      } else {
        LoadingSpinnerService.Failure("Unable to logout successfully.");
      }

      return false;
    }
  }

  @Action
  async LoadCompanyDetails(companyId: string) {
    try {
      const apiClient = ApiFactory.CreateDataClient();
      const result = await apiClient.companies_getCentralCompanyDetails(
        companyId
      );
      this.company_details_loaded(result);
      document.title = result
        ? result.name + " | Client Portal"
        : "Client Portal";
      return true;
    } catch (e) {
      return false;
    }
  }

  @Action
  async RefreshAuthToken() {
    if (!this.loggedInUser) throw new Error();
    this.refresh_session_timer();
    const apiClient = ApiFactory.CreateAuthClient();
    const command = new RefreshTokenCommand();
    command.jwt = this.loggedInUser.token;
    command.refreshToken = this.loggedInUser.refreshToken;
    const result = await apiClient.users_RefreshAuthToken(command);
    if (!result) throw new Error();
    this.user_logged_in(result);
  }

  @Action
  refreshTokenMatches(options: RequestInit): boolean {
    const refreshReq: RefreshTokenCommand = JSON.parse(
      (options.body || "").toString()
    );
    return this.refreshToken === refreshReq.refreshToken;
  }

  @Action
  RefreshSessionTimer() {
    this.refresh_session_timer();
  }
}
